import { z } from 'zod';
import { campaignPlanSchema } from './campaign.js';
// Campaign panel "planning log" — session 1 of plan mode.
//
// When a campaign is drafted via the multi-agent panel (drafter →
// 3 critic personas → synthesizer), this jsonb column carries the
// internal trail so the founder reading the draft sees what Mag
// argued with herself about before landing on the final plan.
//
// Empty when the campaign was drafted via the legacy one-shot path.
export const campaignCriticPersonaSchema = z.enum(['targeting', 'budget', 'creative']);
export const campaignCritiqueSchema = z.object({
    persona: campaignCriticPersonaSchema,
    /** 1-4 specific concerns this persona raised about the draft.
     *  Each concern is a one-line lowercase mag-voice line citing
     *  the deliverable id or budget number the persona is questioning. */
    concerns: z.array(z.string().min(1).max(280)).min(0).max(4),
    /** 0-4 concrete suggestions for what to change. Each suggestion
     *  is a verb-led action ("drop d_meta_broad_001", "lift
     *  brand-exact to $30/day"). */
    suggestions: z.array(z.string().min(1).max(280)).min(0).max(4),
    /** 1-10 — how strongly the persona feels about its concerns. The
     *  synthesizer uses this to weight conflicting feedback. */
    confidence: z.number().int().min(1).max(10),
});
export const campaignPlanningLogSchema = z.object({
    /** Mag's one-line opener about the initial draft, written by the
     *  drafter. ("first pass: brand-exact + competitor comparison
     *  + one meta hook focused on the wizard wound.") */
    draftSummary: z.string().min(1).max(280),
    /** One critique per persona. Order is targeting → budget →
     *  creative for renderer consistency. */
    critiques: z.array(campaignCritiqueSchema).min(0).max(3),
    /** Mag-voice summary of what changed and why, written by the
     *  synthesizer. The founder reads this first; the per-persona
     *  critiques are the "show your work" backup. */
    synthesisNote: z.string().min(1).max(500),
    /** Total LLM cost for the panel run (drafter + 3 critics + synth). */
    totalLlmCostUsd: z.number().min(0),
    /** The plan as the DRAFTER wrote it, before the critics and the
     *  synthesizer touched it. Spec 1bf1c9b8.
     *
     *  Kept because the synthesizer is the panel's slowest stage — 56.8s
     *  and 2844 output tokens in a measured run — and it regenerates the
     *  WHOLE plan rather than patching the draft. Whether that earns its
     *  cost depends on how much it actually changes, and until this field
     *  existed nothing recorded the before-state: the log carried only a
     *  one-line draftSummary, so the question could not be answered from
     *  production data at all and a fresh run had to be paid for to see it.
     *
     *  Optional because every row written before this field, and every
     *  legacy one-shot draft, has no pre-synthesis state to record. */
    preSynthesisDraft: campaignPlanSchema.optional(),
    /** Critics that failed outright and contributed nothing to this plan.
     *
     *  `critiques` is `.min(0)`, so a run where a persona died has always
     *  been schema-legal — which is the problem: a degraded plan and a
     *  clean one were indistinguishable in the stored log. A founder
     *  reading "show your work" deserves to know a voice is missing, and
     *  whoever debugs the panel later needs the reason without digging
     *  through deploy logs.
     *
     *  Optional because every row written before this field has no such
     *  record, and because a clean run omits it rather than storing [].
     *  Empty is not written; absence means "nothing failed". */
    failedCritics: z
        .array(z.object({
        persona: campaignCriticPersonaSchema,
        /** Why it failed — the parse/LLM error, clipped for storage. */
        reason: z.string().min(1).max(280),
    }))
        .min(1)
        .max(3)
        .optional(),
});
//# sourceMappingURL=campaign-planning-log.js.map